import React, { useState, useEffect } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { fetchTemplateCategories } from '~/redux/actions/admin-templates-actions';
import { getAllTemplates } from '~/redux/actions/template-builder';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION, DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE } from '~/utils/constants';
import { debounce } from '~/utils/helperFunctions';

// Components
import Table from './Table';
import CreateModal from './CreateCategoryModal';
import EditCustomFieldModal from './EditCategoryModal';
import DeleteModal from './DeleteCategoryModal';
import AssociateTemplatesModal from './AssociateTemplatesModal';
import NoCategoriesScreen from './NoCategoriesScreen';
import DragAndSortCategories from './DragAndSortCategories';
import OlcButton from '~/components/General/OlcButton';
import OlcSearchInput from '~/components/General/OlcSearchInput';

// MUI Components
import { Box, Grid, Container, useMediaQuery } from '@mui/material';

// Icons
import { InviteCIcon } from '~/assets/images';
import SwapVertIcon from '@mui/icons-material/SwapVert';

const TemplateCategory = () => {
  const [isMounted, setIsMounted] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState(false);
  const [createModal, setcreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [associateTemplateModal, setAssociateTemplateModal] = useState(false);
  const [sortCategoriesModal, setSortCategoriesModal] = useState(false);
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState(null);
  // Use the useMediaQuery hook to check the screen size
  const isXlScreen = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  // Determine the maxWidth based on the screen size
  const maxWidth = isXlScreen ? 'xxl' : 'xl';

  const dispatch = useDispatch();

  const totalCategoriesInDB = useSelector(
    (state) => state.adminTemplatesReducers.totalCategoriesInDB
  );
  const currentMode = useSelector((state) => state.userReducers.user.isLiveMode || false);

  const getAdminCategories = () => {
    let payload = {
      pagination,
      search: search?.length ? search : null,
    };
    dispatch(fetchTemplateCategories(payload));
  };

  const handleSearch = () => {
    if (search) {
      setPagination({ ...pagination, page: 0 });
      setSearchApplied(true);
      getAdminCategories();
    }
  };

  const searchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const debouncedHandleKeyDown = debounce(searchKeyDown, 300);

  useEffect(() => {
    setIsMounted(true);
    getAdminCategories();
    dispatch(getAllTemplates(DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.page, 500));
  }, []);

  useEffect(() => {
    if (!search && isMounted) {
      setSearchApplied(false);
      setSearch('');
      getAdminCategories();
    }
  }, [search]);

  useEffect(() => {
    if (isMounted) {
      getAdminCategories();
    }
  }, [pagination.page]);

  useEffect(() => {
    if (isMounted) {
      setPagination(DEFAULT_PAGINATION);
      getAdminCategories();
    }
  }, [currentMode]);

  const noCategoriesInDb = () => {
    return (
      <Container
        maxWidth={maxWidth}
        className="olcAdminEmptyPage"
        // sx={{
        //   margin: '120px auto 50px',
        // }}
      >
        <NoCategoriesScreen
          openModal={createModal}
          handleCloseModal={() => setcreateModal(false)}
          pagination={pagination}
          setcreateModal={setcreateModal}
        />
      </Container>
    );
  };

  if (totalCategoriesInDB === 0 && totalCategoriesInDB !== null) {
    return noCategoriesInDb();
  }

  return (
    <>
      <Container maxWidth="xxl" className="olcAdminListPage">
        <Grid container className="olcAdminListToolbar olcAdminListToolbar--split">
          <Grid item lg={6} md={8} sm={12} xs={12} className="searchBtnWrapper">
            <Box className="searchWrapper">
              <OlcSearchInput
                type="text"
                value={search}
                searchApplied={searchApplied}
                onChange={(e) => {
                  e.target.value == ' '
                    ? setSearch(e.target.value.trim())
                    : setSearch(e.target.value);
                }}
                onKeyDown={debouncedHandleKeyDown}
                placeholder={`Search by template category`}
                inputProps={{ 'aria-label': 'Search by template category' }}
                onClear={() => setSearch('')}
              />
            </Box>
          </Grid>
          <Grid item lg={6} md={4} sm={12} xs={12}>
            <Box className="createBtn">
              <OlcButton
                variant="secondary"
                startIcon={<SwapVertIcon />}
                onClick={() => setSortCategoriesModal(true)}
              >
                {MESSAGES.ADMIN_DASHBOARD.CATEGORIES.SORT_BTN_TEXT}
              </OlcButton>
              <OlcButton
                variant="primary"
                startIcon={<img src={InviteCIcon} alt="" />}
                onClick={() => setcreateModal(true)}
              >
                {MESSAGES.ADMIN_DASHBOARD.CATEGORIES.CREATE_BTN_TEXT}
              </OlcButton>
            </Box>
          </Grid>
        </Grid>
        <Table
          setEditModal={setEditModal}
          setDeleteModal={setDeleteModal}
          setAssociateTemplateModal={setAssociateTemplateModal}
          setSelectedTemplateCategory={setSelectedTemplateCategory}
          updatePagination={setPagination}
          pagination={pagination}
          searchApplied={searchApplied}
        />
      </Container>
      <CreateModal
        openModal={createModal}
        handleCloseModal={() => setcreateModal(false)}
        pagination={pagination}
      />
      <DeleteModal
        openModal={deleteModal}
        handleCloseModal={() => setDeleteModal(false)}
        selectedTemplateCategory={selectedTemplateCategory}
        pagination={pagination}
      />
      <EditCustomFieldModal
        openModal={editModal}
        handleCloseModal={() => setEditModal(false)}
        selectedTemplateCategory={selectedTemplateCategory}
        pagination={pagination}
      />
      <AssociateTemplatesModal
        openModal={associateTemplateModal}
        handleCloseModal={() => setAssociateTemplateModal(false)}
        selectedTemplateCategory={selectedTemplateCategory}
        pagination={pagination}
      />
      <DragAndSortCategories
        openModal={sortCategoriesModal}
        handleCloseModal={() => setSortCategoriesModal(false)}
        pagination={pagination}
      />
    </>
  );
};

export default TemplateCategory;
